/**
 * DOM manipulation utilities
 */

import type { SelectElement } from './types';
import type { Country, Subdivision } from '@countriesdb/widget-core';
import {
  applyCountryNameFilter,
  sortCountries,
  buildSubdivisionTree,
  flattenSubdivisionOptions,
  parseNestingPrefixes,
} from '@countriesdb/widget-core';

/**
 * Initialize a select element with default option
 */
export function initializeSelect(
  select: SelectElement,
  fallbackLabel: string = 'Not Applicable',
  isSubdivision: boolean = false
): void {
  const isMultiple = select.hasAttribute('multiple');

  // For multi-select, don't add default option or use data-label/data-default-value
  if (isMultiple) {
    select.innerHTML = '';
    // Remove data-label and data-default-value for multi-select (they're ignored)
    if (select.dataset.label !== undefined) {
      delete select.dataset.label;
    }
    if (select.dataset.defaultValue !== undefined) {
      delete select.dataset.defaultValue;
    }
  } else {
    const dataLabel = select.dataset.label;
    const dataDefaultValue = select.dataset.defaultValue;

    const label = dataLabel || fallbackLabel;
    const defaultValue = dataDefaultValue ?? '';
    select.innerHTML = `<option value="${defaultValue}">${label}</option>`;

    if (dataLabel !== undefined) {
      select.dataset.label = dataLabel;
    }
    if (dataDefaultValue !== undefined) {
      select.dataset.defaultValue = dataDefaultValue;
    }
  }

  if (isSubdivision) {
    select.disabled = true;
  }
}

/**
 * Populate a select element with countries
 */
export function populateCountrySelect(
  select: SelectElement,
  countries: Country[],
  language: string,
  countryNameFilter?: (code: string, name: string, lang: string, item: Country) => string | false | null | undefined
): void {
  // Apply filter and get display names
  let filteredCountries = applyCountryNameFilter(countries, countryNameFilter, language);
  
  // Sort if filter was applied
  if (countryNameFilter) {
    filteredCountries = sortCountries(filteredCountries, language);
  }

  // Check for multi-select using both attribute and property
  const isMultiple = select.hasAttribute('multiple') || select.multiple;
  
  if (isMultiple) {
    // For multi-select, don't add default option (like old widget)
    select.innerHTML = '';
  } else {
    // Clear existing options (except default)
    const defaultOption = select.querySelector('option[value=""]') || 
      (select.dataset.defaultValue ? select.querySelector(`option[value="${select.dataset.defaultValue}"]`) : null);
    const defaultValue = select.dataset.defaultValue ?? '';
    
    // Keep default option if it exists
    select.innerHTML = defaultOption ? defaultOption.outerHTML : `<option value="${defaultValue}">${select.dataset.label || '&mdash;'}</option>`;
  }

  // Add country options
  filteredCountries.forEach((country) => {
    const option = document.createElement('option');
    option.value = country.iso_alpha_2;
    option.textContent = country._displayName;
    select.appendChild(option);
  });
}

/**
 * Populate a select element with subdivisions
 */
export function buildSubdivisionOptionsHTML(
  subdivisions: Subdivision[],
  select: SelectElement,
  language: string,
  showSubdivisionType: boolean,
  allowParentSelection: boolean,
  subdivisionNameFilter?: (
    code: string,
    originalName: string,
    language: string,
    countryCode: string | null,
    item: Subdivision
  ) => string | false | null | undefined
): string {
  const tree = buildSubdivisionTree(subdivisions);
  
  // Parse nesting prefixes from data attributes
  const dataAttributes: Record<string, string | undefined> = {};
  for (let lvl = 1; lvl <= 10; lvl++) {
    const key = `nested${lvl}Prefix`;
    const value = select.dataset[key];
    if (value !== undefined) {
      dataAttributes[`nested${lvl}Prefix`] = value;
    } else {
      break;
    }
  }
  const prefixes = parseNestingPrefixes(dataAttributes);
  
  const isMultiple = select.hasAttribute('multiple');
  const labelKey = showSubdivisionType ? 'full_name' : 'name';

  let html = '';
  // For multi-select, don't add default option
  if (!isMultiple) {
    const defaultLabel = select.dataset.label || '&mdash;';
    const defaultValue = select.dataset.defaultValue ?? '';
    html = `<option value="${defaultValue}">${defaultLabel}</option>`;
  }

  html += flattenSubdivisionOptions(
    tree,
    0,
    prefixes,
    labelKey,
    language,
    allowParentSelection,
    subdivisionNameFilter
  );
  
  return html;
}

/**
 * Apply preselected value to a select element
 * Like old widget, reads the value from the select element itself to get current state
 */
export function applyPreselectedValue(
  select: SelectElement,
  apiKey?: string
): void {
  // Read from select element like old widget (line 740)
  const tempOnce = select.dataset._widgetTempPreselect;
  const permanent = select.getAttribute('data-preselected') || select.dataset.preselected;
  const chosen = (tempOnce !== undefined && tempOnce !== null && String(tempOnce).trim() !== '') 
    ? tempOnce 
    : permanent;
  
  if (!chosen || (typeof chosen === 'string' && chosen.trim() === '')) {
    return;
  }

  const value = String(chosen);
  const isMultiple = select.hasAttribute('multiple');

  if (isMultiple) {
    // For multi-select, parse comma-separated values
    const values = value
      .split(',')
      .map((v) => v.trim())
      .filter((v) => v !== '');
    // Select all matching options
    Array.from(select.options).forEach((option) => {
      option.selected = values.includes(option.value);
    });
  } else {
    // Single select: set single value
    select.value = value;
  }
  
  // Consume preselect so it's only applied once (like old widget)
  if (select.dataset._widgetTempPreselect !== undefined) {
    delete select.dataset._widgetTempPreselect;
  }
  if (select.dataset.preselected !== undefined) {
    delete select.dataset.preselected;
  }
  if (select.hasAttribute('data-preselected')) {
    select.removeAttribute('data-preselected');
  }
}

/**
 * Handle API error by showing error message in select
 */
export function handleApiError(
  select: SelectElement,
  errorMessage: string | Error,
  replace: boolean = false
): void {
  const message = errorMessage instanceof Error ? errorMessage.message : errorMessage;
  const defaultValue = select.dataset.defaultValue ?? '';
  // Add "Error: " prefix to match old widget behavior and test expectations
  const formattedMessage = message.startsWith('Error: ') ? message : `Error: ${message}`;
  if (replace) {
    select.innerHTML = `<option value="${defaultValue}" disabled>${formattedMessage}</option>`;
  } else {
    select.innerHTML += `<option value="${defaultValue}" disabled>${formattedMessage}</option>`;
  }
  // Ensure select is enabled so users can see the error
  select.disabled = false;
}

/**
 * Parse boolean from string value
 */
export function parseBoolean(value: string | null | undefined): boolean {
  if (value === null || value === undefined) {
    return false;
  }
  const lowered = String(value).trim().toLowerCase();
  return !(lowered === '0' || lowered === 'false');
}

